import { useState, useEffect } from 'react';
import { X, Save, ChevronDown } from 'lucide-react';
import { updatePostCategory, bulkUpdateCategory, type Category } from '../services/api';
import type { Post } from '../types';
import { getAuthorInfo } from '@/lib/postUtils';

interface CategoryChangePopupProps {
    /** Single-post mode: the post being categorised. Omit when using postIds (bulk mode). */
    post?: Post;
    /** Bulk mode: ids of the posts being categorised together. Takes precedence over `post`. */
    postIds?: number[];
    isOpen: boolean;
    onClose: () => void;
    onCategoryUpdate?: (postId: number, category?: any) => void;
    /** Called after a successful bulk save with the ids that were requested and any that failed. */
    onBulkCategoryUpdate?: (postIds: number[], category: any, failedIds: number[]) => void;
    categories: Category[];
}

export const CategoryChangePopup = ({ post, postIds, isOpen, onClose, onCategoryUpdate, onBulkCategoryUpdate, categories }: CategoryChangePopupProps) => {
    const isBulkMode = !!postIds && postIds.length > 0;
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
    const [customLabel, setCustomLabel] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showFullContent, setShowFullContent] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (isBulkMode) {
                // Bulk mode covers posts with potentially different existing categories,
                // so start from a blank slate rather than prefilling from any single post.
                setSelectedCategoryId('');
                setCustomLabel('');
            } else if (post?.category) {
                // Set initial selected category
                setSelectedCategoryId(post.category.id);
                if (post.category.category_enum === 'others') {
                    setCustomLabel(post.category.label || '');
                }
            }
        }
    }, [isOpen, post, isBulkMode]);

    const handleSave = async () => {
        if (!selectedCategoryId) return;

        try {
            setIsLoading(true);
            const selectedCategory = categories.find(cat => cat.id === selectedCategoryId);

            if (!selectedCategory) return;

            const label = selectedCategory.category_enum === 'others' && customLabel.trim()
                ? customLabel.trim()
                : undefined;

            const updatedCategory = {
                id: selectedCategory.id,
                category_enum: selectedCategory.category_enum,
                category_display_value: selectedCategory.category_display_value,
                label: selectedCategory.category_enum === 'others' ? (label || '') : selectedCategory.category_display_value
            };

            if (isBulkMode && postIds) {
                const response = await bulkUpdateCategory(postIds, parseInt(selectedCategoryId), label);
                onBulkCategoryUpdate?.(postIds, updatedCategory, response.failed_ids);
            } else if (post) {
                const payload: {
                    post_id: number;
                    category_id: number;
                    label?: string;
                } = {
                    post_id: post.id,
                    category_id: parseInt(selectedCategoryId),
                };

                if (label) {
                    payload.label = label;
                }

                await updatePostCategory(payload);

                // Call the callback to update the parent component
                onCategoryUpdate?.(post.id, updatedCategory);
            }

            onClose();
        } catch (error) {
            console.error('Failed to update category:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const hasChanges = () => {
        if (isBulkMode) {
            return selectedCategoryId !== '';
        }

        if (!post?.category) {
            return selectedCategoryId !== '';
        }

        if (selectedCategoryId !== post.category.id) {
            return true;
        }

        if (post.category.category_enum === 'others' && selectedCategoryId === post.category.id) {
            return customLabel !== (post.category.label || '');
        }

        return false;
    };

    const getPostContent = () => {
        if (!post) return '';
        if (post.platform === 'INSTAGRAM' && post.instagram_post) {
            return post.instagram_post.caption || '';
        } else if (post.platform === 'X' && post.x_post) {
            return post.x_post.content || '';
        } else if (post.platform === 'LINKEDIN' && post.linkedin_post) {
            return post.linkedin_post.content || '';
        } else if (post.platform === 'TIKTOK' && post.tiktok_post) {
            return post.tiktok_post.content || '';
        } else if (post.platform === 'REDDIT' && post.reddit_post) {
            return post.reddit_post.content || '';
        } else if (post.platform === 'CHROME_BOOKMARK' && post.chrome_bookmark_post) {
            return post.chrome_bookmark_post.og_title || post.chrome_bookmark_post.title || post.chrome_bookmark_post.url || '';
        }
        return '';
    };

    const authorInfo = !isBulkMode && post ? getAuthorInfo(post) : null;
    const postContent = getPostContent();
    const currentCategory = !isBulkMode ? post?.category : undefined;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-700">
                    <h3 className="text-lg font-semibold text-white">
                        {isBulkMode ? `Change Category for ${postIds?.length} posts` : 'Change Category'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white bg-transparent transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex flex-col bg-gray-700/50 rounded-lg m-4 pb-4">
                    <div>
                        {isBulkMode ? (
                            <div className="p-3 text-sm text-gray-300">
                                The selected category will be applied to all <span className="font-medium text-white">{postIds?.length}</span> selected posts, replacing any existing category on those posts.
                            </div>
                        ) : (
                            <>
                                {/* Author Info */}
                                <div className="flex items-center space-x-3 p-3 ">
                                    {authorInfo && (
                                        <div className="flex items-center space-x-3 p-3 ">
                                            <img
                                                src={authorInfo.avatar || "/assets/placeholder.jpg"}
                                                alt={authorInfo.name}
                                                className="w-10 h-10 rounded-full"
                                                onError={(e) => {
                                                    e.currentTarget.src = "/assets/placeholder.jpg";
                                                }}
                                            />
                                            <div>
                                                <div className="font-medium text-white">{authorInfo.name}</div>
                                                <div className="text-sm text-gray-400">{authorInfo.handle}</div>
                                            </div>

                                        </div>
                                    )}

                                    {currentCategory && (
                                        <div className="flex-1 flex justify-end">
                                            <button
                                                className="hidden md:inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden hover:bg-purple-500/30 bg-purple-500/20 text-purple-300 border-purple-500/30 cursor-pointer"
                                            >
                                                {currentCategory.category_display_value}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {postContent && (
                                    <div className="text-white text-sm mx-4">
                                        {postContent.length > 500 ? (
                                            <div>
                                                <div className="whitespace-pre-line">
                                                    {showFullContent ? postContent : `${postContent.slice(0, 500)}...`}
                                                    <p
                                                        onClick={() => setShowFullContent(!showFullContent)}
                                                        className="text-purple-400 bg-transparent hover:text-purple-300 text-xs mt-2 underline cursor-pointer">
                                                        {showFullContent ? 'Show less' : 'Show more'}
                                                    </p>
                                                </div>

                                            </div>
                                        ) : (
                                            <div className="whitespace-pre-line">{postContent}</div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* New Category Selection */}
                    <div className="space-y-3 mx-4 mt-4">
                        <label className="text-sm text-gray-300">Select New Category</label>

                        <div className="relative">
                            <>
                                <button
                                    type="button"
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    className="w-full flex items-center justify-between p-3 bg-gray-700 border border-gray-600 rounded-lg text-white hover:border-gray-500 transition-colors"
                                >
                                    <span className={selectedCategoryId ? 'text-white' : 'text-gray-400'}>
                                        {selectedCategoryId
                                            ? categories.find(cat => cat.id === selectedCategoryId)?.category_display_value
                                            : 'Choose a category'
                                        }
                                    </span>
                                    <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {isDropdownOpen && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                                        <div className="py-1">
                                            {categories
                                                .filter(category => category.category_enum !== 'all')
                                                .map((category) => (
                                                    <button
                                                        key={category.id}
                                                        onClick={() => {
                                                            setSelectedCategoryId(category.id);
                                                            setIsDropdownOpen(false);
                                                            // Clear custom label if not "others" category
                                                            if (category.category_enum !== 'others') {
                                                                setCustomLabel('');
                                                            }
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-sm text-white bg-transparent hover:bg-gray-600 transition-colors"
                                                    >
                                                        {category.category_display_value}
                                                    </button>
                                                ))}
                                        </div>
                                    </div>
                                )}
                            </>

                        </div>

                        {/* Custom Label Input for "others" category */}
                        {selectedCategoryId && categories.find(cat => cat.id === selectedCategoryId)?.category_enum === 'others' && (
                            <div className="space-y-2 mb-4">
                                <label className="text-sm text-gray-300">Custom Label</label>
                                <input
                                    type="text"
                                    value={customLabel}
                                    onChange={(e) => setCustomLabel(e.target.value)}
                                    placeholder="Enter custom label"
                                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none transition-colors"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-700">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-transparent text-gray-400 hover:text-white transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!hasChanges() || isLoading || !selectedCategoryId}
                        className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
                    >
                        {isLoading ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        <span>Save</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
